<script lang="ts">
  import { icon } from '#app/lib/icons'
  import { userContent } from '#app/lib/user_content'
  import Dropdown from '#components/dropdown.svelte'
  import Modal from '#components/modal.svelte'
  import { getVisibilitySummary, getVisibilitySummaryLabel, visibilitySummariesData } from '#general/lib/visibility'
  import ListingEditor from '#listings/components/listing_editor.svelte'
  import { i18nTypesKeys } from '#listings/lib/entities_typing'
  import type { SerializedListing } from '#listings/lib/listings'
  import { i18n } from '#user/lib/i18n'
  import UserInfobox from '#users/components/user_infobox.svelte'
  import type { SerializedUser } from '#users/lib/users'

  let {
    listing = $bindable(),
    isEditable,
    creator,
  }: {
    listing: SerializedListing
    isEditable: boolean
    creator: SerializedUser
  } = $props()

  const { type = 'work' } = $state(listing)

  let showListEditorModal = $state(false)

  const username = $derived(creator.username)
  const picture = $derived(creator.picture)
  const userListingsPathname = $derived(creator.listingsPathname)

  const i18nTypeKey = i18nTypesKeys[type]

  const name = $derived(listing.name)
  const description = $derived(listing.description)
  const visibility = $derived(listing.visibility)
  const visibilitySummary = $derived(getVisibilitySummary(visibility))
  const visibilitySummaryIcon = $derived(visibilitySummariesData[visibilitySummary].icon)
  const visibilitySummaryLabel = $derived(i18n(getVisibilitySummaryLabel(visibility)))
</script>

<div class="listing-info">
  <div class="header">
    <div class="first-row">
      <h2>{name}</h2>
      {#if isEditable}
        <Dropdown
          align="right"
          buttonTitle={i18n('Show actions')}
          clickOnContentShouldCloseDropdown={true}
          dropdownWidthBaseInEm={20}
        >
          {#snippet buttonInner()}
            <div class="button-inner">
              {@html icon('cog')}
            </div>
          {/snippet}
          {#snippet dropdownContent()}
            <ul class="dropdown-content">
              <li>
                <button
                  aria-haspopup="dialog"
                  onclick={() => showListEditorModal = true}
                >
                  {@html icon('pencil')}
                  {i18n('Edit list info')}
                </button>
              </li>
            </ul>
          {/snippet}
        </Dropdown>
      {/if}
    </div>
    {#if description}
      <p>{@html userContent(description)}</p>
    {/if}
  </div>
  <div class="creator-row">
    <UserInfobox
      linkUrl={userListingsPathname}
      linkTitle={i18n('see_all_listings_by_user', { username })}
      label={i18n('List creator')}
      name={username}
      {picture}
    />
    <div class="right-section">
      <div class="listing-type">
        <span class="label">{i18n('Type')}</span>
        <span class="visibility-indicator">
          {i18n(i18nTypeKey)}
        </span>
      </div>
      <div class="visibility">
        <span class="label">{i18n('Visible by')}</span>
        <span class="visibility-indicator">
          {@html icon(visibilitySummaryIcon)} {visibilitySummaryLabel}
        </span>
      </div>
    </div>
  </div>
</div>

{#if showListEditorModal}
  <Modal onCloseModal={() => showListEditorModal = false}>
    <ListingEditor
      bind:listing
      onListingEditorDone={() => showListEditorModal = false}
    />
  </Modal>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .listing-info{
    align-self: stretch;
    margin: 0.5em;
    margin-block-end: 1em;
    padding: 0.5em 1em 1em;
    @include radius;
    background-color: $light-grey;
    text-wrap: balance;
  }
  h2{
    margin-block-start: 0;
    font-weight: bold;
  }
  .first-row{
    @include display-flex(row, center, space-between);
    width: 100%;
  }
  .header{
    @include display-flex(column, baseline);
  }
  .creator-row{
    margin-block-start: 1em;
    @include display-flex(row, center, space-between);
  }
  .label{
    color: $label-grey;
    display: block;
  }
  .button-inner{
    @include tiny-button($grey);
    padding: 0.5em;
  }
  .dropdown-content{
    min-width: min(10em, 100vw);
    @include shy-border;
    background-color: white;
    @include radius;
    position: relative;
    :global(li){
      @include display-flex(row, stretch, flex-start);
      &:not(:last-child){
        margin-block-end: 0.2em;
      }
    }
    :global(button){
      flex: 1;
      @include display-flex(row, center, flex-start);
      min-block-size: 3em;
      @include bg-hover(white, 10%);
      padding: 0 1em;
      text-align: start;
    }
    :global(.fa){
      margin-inline-end: 0.5rem;
    }
  }
  .right-section{
    text-align: center;
  }
  .listing-type{
    padding-block-end: 0.5em;
  }
  .visibility{
    padding: 0.2em;
    @include radius;
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    .listing-info{
      padding: 1em;
      margin: 0;
    }
    .button-inner{
      padding: 0.3em;
    }
  }
  /* Smaller screens */
  @media screen and (width < $smaller-screen){
    .first-row{
      @include display-flex(column-reverse);
    }
  }
</style>
